import { useState, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import { Info, Pencil, Check, ChevronDown, ChevronUp, ChevronRight, ChevronsDown, ChevronsRight, Filter, Clock, ShoppingCart, X, Plus, Trash2, ClipboardList, CheckSquare, Maximize2, Square, RefreshCw, Minus } from 'lucide-react';
import ConfirmationModal from '@/components/ConfirmationModal';
import ModalBackdrop from '@/components/ModalBackdrop';
import PremiumRequiredModal from '@/components/PremiumRequiredModal';
import AddToShoppingListModal from '@/components/AddToShoppingListModal';
import { areaConfig } from '@/config/area-config';
import { useSettingsStore, type TimeLevel } from '@/stores/settings-store';
import { useGamificationStore, POINT_VALUES, type PrepArea } from '@/stores/gamification-store';
import { useChecklistStore, useChecklistHydrated } from '@/stores/checklist-store';
import { useAuthStore, useAuthHydrated } from '@/stores/auth-store';
import { useFamilyStore } from '@/stores/family-store';
import { lockScroll, unlockScroll } from '@/utils/scrollLock';
import { defaultChecklists } from '@/data/default-checklists';
import useCustomChecklistStore from '@/stores/custom-checklist-store';

// Available colors for custom lists
const LIST_COLORS = [
  '#ff6b35', // Orange (default)
  '#7fa17a', // Sage green
  '#16bcf9', // Blue
  '#d17da4', // Rose pink
  '#f91680', // Pink
  '#f9f116', // Yellow
  '#b51818', // Deep red
  '#8f16f9', // Purple
];

// Use shared areaConfig

type AreaKey = keyof typeof areaConfig;

interface ChecklistItem {
  id: string;
  text: string;
  area: AreaKey;
  level: TimeLevel;
  description?: string;
}

const LEVEL_SORT_ORDER: Record<TimeLevel, number> = {
  '7d': 0,
  '30d': 1,
  '90d': 2,
};

// Premium limits for custom checklists
const PREMIUM_LISTS = 50; // Premium users can create up to 50 custom lists

export default function ChecklistsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getTimeLevelsForGoal } = useSettingsStore();
  const { addPoints } = useGamificationStore();
  const { user, isAuthenticated } = useAuthStore();
  const family = useFamilyStore((s) => s.family);
  const authHydrated = useAuthHydrated();
  const checklistHydrated = useChecklistHydrated();
  const {
    isItemCompleted,
    toggleItem: toggleChecklistItem,
    fetchFromServer,
  } = useChecklistStore();
  
  // Premium access check
  const hasPremiumAccess = user?.subscriptionTier === 'premium' || !!family;

    // Router hooks
    const location = useLocation();

    // Filter states
    const areaFilter = searchParams.get('area') as AreaKey | null;
    const [levelFilter, setLevelFilter] = useState<TimeLevel | 'all'>('all');
    const [showCompleted, setShowCompleted] = useState(true);
    const [expandedAreas, setExpandedAreas] = useState<Set<AreaKey>>(() => {
      try {
        const saved = localStorage.getItem('checklistsExpandedDefaultAreas');
        if (!saved) return new Set<AreaKey>();
        const parsed = JSON.parse(saved);
        if (!Array.isArray(parsed)) return new Set<AreaKey>();
        const validAreaKeys = new Set(Object.keys(areaConfig));
        return new Set<AreaKey>(parsed.filter((area): area is AreaKey => typeof area === 'string' && validAreaKeys.has(area)));
      } catch {
        return new Set<AreaKey>();
      }
    });
      // Which custom lists are expanded (start closed per requirement)
      const [expandedCustomLists, setExpandedCustomLists] = useState<Set<string>>(() => {
        try {
          const saved = localStorage.getItem('checklistsExpandedCustomLists');
          if (!saved) return new Set<string>();
          const parsed = JSON.parse(saved);
          if (!Array.isArray(parsed)) return new Set<string>();
          return new Set<string>(parsed.filter((listId): listId is string => typeof listId === 'string'));
        } catch {
          return new Set<string>();
        }
      });
    
    // Shopping list modal state (gemensam komponent)
    const [showShoppingModal, setShowShoppingModal] = useState(false);
    const [shoppingItemName, setShoppingItemName] = useState<string | null>(null);
    const [newListShowOnDashboard, setNewListShowOnDashboard] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [newListRecurring, setNewListRecurring] = useState(false);
    const [newListRecurringType, setNewListRecurringType] = useState<'daily' | 'weekly'>('daily');
    const [newListRecurringTime, setNewListRecurringTime] = useState(0);
    const [newListRecurringDay, setNewListRecurringDay] = useState(0);
    const [newListColor, setNewListColor] = useState(LIST_COLORS[0]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    
    const customLists = useCustomChecklistStore(state => state.lists);
  const isSyncing = useCustomChecklistStore(state => state.isSyncing);
  const createCustomList = useCustomChecklistStore(state => state.createList);
    const deleteCustomList = useCustomChecklistStore(state => state.deleteList);
    const createCustomItem = useCustomChecklistStore(state => state.createItem);
    const updateCustomItem = useCustomChecklistStore(state => state.updateItem);
    const deleteCustomItem = useCustomChecklistStore(state => state.deleteItem);
    const toggleCustomItem = useCustomChecklistStore(state => state.toggleItem);
    const clearCompletedForList = useCustomChecklistStore(state => state.clearCompletedForList);
    const uncheckAllForList = useCustomChecklistStore(state => state.uncheckAllForList);
    const getCustomItemsForList = useCustomChecklistStore(state => state.getItemsForList);
    const fetchCustomFromServer = useCustomChecklistStore(state => state.fetchFromServer);
    const updateCustomList = useCustomChecklistStore(state => state.updateList);
    const checkRecurring = useCustomChecklistStore(state => state.checkRecurring);
    // subscribe to items so the component updates when items change
    useCustomChecklistStore(state => state.items);

    // Rename modal state
    const [renamingListId, setRenamingListId] = useState<string | null>(null);
    const [renamingListName, setRenamingListName] = useState('');
    const [renamingListShowOnDashboard, setRenamingListShowOnDashboard] = useState(false);
    const [renamingListRecurring, setRenamingListRecurring] = useState(false);
    const [renamingListRecurringType, setRenamingListRecurringType] = useState<'daily' | 'weekly'>('daily');
    const [renamingListRecurringTime, setRenamingListRecurringTime] = useState(0);
    const [renamingListRecurringDay, setRenamingListRecurringDay] = useState(0);
    const [renamingListColor, setRenamingListColor] = useState(LIST_COLORS[0]);
    // Delete confirmation state
    const [deletingListId, setDeletingListId] = useState<string | null>(null);
    const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
    // Clear completed confirmation state
    const [confirmClearListId, setConfirmClearListId] = useState<string | null>(null);
    // Uncheck all confirmation state
    const [confirmUncheckAllListId, setConfirmUncheckAllListId] = useState<string | null>(null);
    // Edit item modal state
    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editingItemText, setEditingItemText] = useState('');
    // Premium modal state
    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [premiumFeature, setPremiumFeature] = useState<string>('');
    // Fullscreen modal state
    const [fullscreenListId, setFullscreenListId] = useState<string | null>(null);
    
    // Section expansion states (saved to localStorage)
    const [expandedCustomSection, setExpandedCustomSection] = useState<boolean>(() => {
      try {
        const saved = localStorage.getItem('checklistsExpandedCustomSection');
        // Default: Egna listor-sektionen ska vara öppen första gången
        return saved !== null ? JSON.parse(saved) : true;
      } catch {
        return true;
      }
    });
    const [expandedDefaultSection, setExpandedDefaultSection] = useState<boolean>(() => {
      try {
        const saved = localStorage.getItem('checklistsExpandedDefaultSection');
        // Default: Standard-sektionen ska vara öppen första gången
        return saved !== null ? JSON.parse(saved) : true;
      } catch {
        return true;
      }
    });

    // Hämta relevanta tidsnivåer baserat på beredskapsmål
    const relevantLevels = getTimeLevelsForGoal();

    // Använd de hårdkodade checklistorna
    const items = defaultChecklists as ChecklistItem[];
    
    // Count completed items in default checklists for "Ny lista" button visibility
    const completedDefaultItemsCount = useMemo(() => {
      return items.filter(item => isItemCompleted(item.id)).length;
    }, [items, isItemCompleted]);

    // Vilka kategorier/områden finns i checklistan (används i filterdropdown)
    const availableAreas = Array.from(new Set(items.map(i => i.area))) as AreaKey[];

    // Mappa checklist area till gamification area
    const mapAreaToGamification = (area: AreaKey): PrepArea => {
      const mapping: Record<AreaKey, PrepArea> = {
        health: 'health',
        energy: 'energy',
        food: 'food',
        shelter: 'shelter',
        communication: 'communication',
        transport: 'transport',
        water: 'water',
        information: 'information',
        safety: 'safety',
      };
      return mapping[area];
    };

    // Hämta data från servern vid mount om användaren är inloggad (vänta på BÅDA hydrations)
    useEffect(() => {
      if (authHydrated && checklistHydrated && isAuthenticated) {
        fetchFromServer();
        // Fetch custom user lists as well
        fetchCustomFromServer();
        // Check for recurring lists that need to be reset
        checkRecurring();
      }
    }, [authHydrated, checklistHydrated, isAuthenticated, fetchFromServer, fetchCustomFromServer, checkRecurring]);

    // Re-sync custom checklists when family changes (join/leave)
    useEffect(() => {
      if (authHydrated && checklistHydrated && isAuthenticated) {
        const clearAndSync = useCustomChecklistStore.getState().clearAndSync;
        clearAndSync();
      }
    }, [family?.id, authHydrated, checklistHydrated, isAuthenticated]);

    // Expandera område om det finns i query-param `area`
    useEffect(() => {
      if (areaFilter) {
        setExpandedAreas((prev) => new Set(Array.from(prev).concat(areaFilter as AreaKey)));
      }
    }, [areaFilter]);
    
    // Save section expansion state to localStorage
    useEffect(() => {
      try {
        localStorage.setItem('checklistsExpandedCustomSection', JSON.stringify(expandedCustomSection));
      } catch {
        // Ignore storage errors
      }
    }, [expandedCustomSection]);
    
    useEffect(() => {
      try {
        localStorage.setItem('checklistsExpandedDefaultSection', JSON.stringify(expandedDefaultSection));
      } catch {
        // Ignore storage errors
      }
    }, [expandedDefaultSection]);

    useEffect(() => {
      try {
        localStorage.setItem('checklistsExpandedDefaultAreas', JSON.stringify(Array.from(expandedAreas)));
      } catch {
        // Ignore storage errors
      }
    }, [expandedAreas]);

    useEffect(() => {
      try {
        localStorage.setItem('checklistsExpandedCustomLists', JSON.stringify(Array.from(expandedCustomLists)));
      } catch {
        // Ignore storage errors
      }
    }, [expandedCustomLists]);

    // Expandera och scrolla till egen checklista om det finns i location state
    useEffect(() => {
      const state = location.state as { scrollToListId?: string } | null;
      const customListId = state?.scrollToListId;
      
      if (customListId && customLists.some(list => list.id === customListId)) {
        // Expandera listan
        setExpandedCustomLists((prev) => {
          const newSet = new Set(prev);
          newSet.add(customListId);
          return newSet;
        });
        
        // Scrolla till listan efter en kort fördröjning
        const timeoutId = setTimeout(() => {
          const element = document.getElementById(`custom-list-${customListId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'auto', block: 'start' });
          }
        }, 100);
        
        // Rensa state efter scrollning
        window.history.replaceState({}, document.title);
        
        return () => clearTimeout(timeoutId);
      }
    }, [location.state, customLists]);

    // Blockera scrolling när modal är öppen (använder delad scrollLock)
    // Note: ModalBackdrop-based modals (confirmUncheckAllListId, confirmClearListId) handle their own scroll locking
    useLayoutEffect(() => {
      const isModalOpen = showCreateModal || showShoppingModal || !!renamingListId || !!deletingListId || !!deletingItemId || !!editingItemId || !!fullscreenListId || showPremiumModal;

      if (isModalOpen) {
        const y = window.scrollY || window.pageYOffset || 0;
        try { document.body.dataset.prevScrollY = String(y); } catch (_e) { void _e; }
        try { lockScroll(); } catch (_e) { try { document.body.style.overflow = 'hidden'; } catch { /* noop */ } }
        try { requestAnimationFrame(() => { try { window.scrollTo(0, y); } catch { /* noop */ } }); } catch { /* noop */ }
      } else {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      }

      return () => {
        try { unlockScroll(); } catch (_e) { try { document.body.style.overflow = ''; document.documentElement.style.overflow = ''; } catch { /* noop */ } }
      };
    }, [showCreateModal, showShoppingModal, renamingListId, deletingListId, deletingItemId, editingItemId, fullscreenListId, showPremiumModal]);

    // Toggle area expansion
    const toggleAreaExpansion = (area: AreaKey) => {
      const newExpanded = new Set(expandedAreas);
      const isOpening = !newExpanded.has(area);
      if (newExpanded.has(area)) {
        newExpanded.delete(area);
      } else {
        newExpanded.add(area);
      }
      setExpandedAreas(newExpanded);
      if (isOpening) {
        setTimeout(() => {
          const el = document.getElementById(`area-${area}`);
          if (el) {
            const offset = window.innerWidth < 768 ? 64 : 16;
            const top = el.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        }, 50);
      }
    };

    const scrollSectionIntoView = (sectionId: string) => {
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) {
          const offset = window.innerWidth < 768 ? 64 : 16;
          const top = el.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 50);
    };

    // Toggle item checked
    const toggleItem = (id: string) => {
      const item = items.find(i => i.id === id);
      if (!item) return;

      const wasChecked = isItemCompleted(id);

      // Toggla i storen (synkas automatiskt)
      toggleChecklistItem(id, item.area as PrepArea);

      // Ge poäng när man bockar av (inte när man avbockar)
      if (!wasChecked) {
        const gamificationArea = mapAreaToGamification(item.area);
        addPoints('resource', gamificationArea, POINT_VALUES.CHECKLIST_ITEM, `Bockade av: ${item.text}`);
      }
    };

    // Filter and group items - RESPEKTERAR BEREDSKAPSMÅL
    const filteredItems = useMemo(() => {
      return items.filter(item => {
        // Filtrera bort items som är utanför användarens beredskapsmål
        if (!relevantLevels.includes(item.level)) return false;
        if (areaFilter && item.area !== areaFilter) return false;
        if (levelFilter !== 'all' && item.level !== levelFilter) return false;
        if (!showCompleted && isItemCompleted(item.id)) return false;
        return true;
      });
    }, [items, areaFilter, levelFilter, showCompleted, relevantLevels, isItemCompleted]);

    // Group by area
    const groupedItems = useMemo(() => {
      const groups = {} as Record<AreaKey, ChecklistItem[]>;
      for (const key of Object.keys(areaConfig) as AreaKey[]) {
        groups[key] = filteredItems
          .filter(item => item.area === key)
          .sort((a, b) => LEVEL_SORT_ORDER[a.level] - LEVEL_SORT_ORDER[b.level]);
      }
      return groups;
    }, [filteredItems]);
    
    // Count total default lists (number of areas with items)
    const defaultListsCount = useMemo(() => {
      return (Object.keys(areaConfig) as AreaKey[]).filter(area => groupedItems[area].length > 0).length;
    }, [groupedItems]);

    const areasWithItems = useMemo(() => {
      return (Object.keys(areaConfig) as AreaKey[]).filter((area) => groupedItems[area].length > 0);
    }, [groupedItems]);

    const allDefaultAreasExpanded = useMemo(() => {
      return areasWithItems.length > 0 && areasWithItems.every((area) => expandedAreas.has(area));
    }, [areasWithItems, expandedAreas]);

    const allCustomListsExpanded = useMemo(() => {
      return customLists.length > 0 && customLists.every((list) => expandedCustomLists.has(list.id));
    }, [customLists, expandedCustomLists]);

    const levelLabels: Record<TimeLevel, string> = {
      '7d': t('checklists.levels.7d'),
      '30d': t('checklists.levels.30d'),
      '90d': t('checklists.levels.90d'),
    };

    const levelColors: Record<TimeLevel, string> = {
      '7d': 'bg-green-500/20 text-green-400 border-green-500/30',
      '30d': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      '90d': 'bg-red-500/20 text-red-400 border-red-500/30',
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center"><CheckSquare className="h-6 w-6 text-primary mr-3 shrink-0" />{t('checklists.title')}</h1>
          <p className="text-muted-foreground mt-1">Jobba strukturerat med beredskapshöjande åtgärder och skaffa viktig utrustning.</p>
        </div>

        {/* Filters row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <div className="flex flex-1 flex-wrap gap-2 sm:flex-nowrap">
              <ChecklistAreaFilterDropdown
                value={areaFilter ?? 'all'}
                onChange={(v) => {
                  const params = new URLSearchParams(searchParams);
                  if (v === 'all') params.delete('area');
                  else params.set('area', v);
                  setSearchParams(params);
                }}
                areas={availableAreas}
              />

              <ChecklistLevelFilterDropdown
                value={levelFilter}
                onChange={(v) => setLevelFilter(v)}
                levels={relevantLevels}
                labels={levelLabels}
              />
            </div>
          </div>

        </div>

        {/* Show completed toggle (left-aligned row) */}
        <div className="mt-2">
          <label className="flex items-center gap-2 text-sm">
            <div
              role="checkbox"
              tabIndex={0}
              aria-checked={showCompleted}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowCompleted(!showCompleted);
                }
              }}
              onClick={() => setShowCompleted(!showCompleted)}
              className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                showCompleted ? 'border-orange-500 bg-orange-500' : 'border-muted-foreground hover:border-orange-500'
              }`}
            >
              {showCompleted && <Check className="h-3 w-3 text-white" />}
            </div>
            <span className="cursor-pointer select-none" onClick={() => setShowCompleted(!showCompleted)}>
              {t('checklists.filter.showCompleted')}
            </span>
          </label>
        </div>
        {/* Egna listor (user-created) */}
        <div className="space-y-3">
          {/* Skeleton loader när egna listor laddas */}
          {isSyncing && customLists.length === 0 && !areaFilter && levelFilter === 'all' && isAuthenticated && completedDefaultItemsCount >= 3 && (
            <div id="custom-checklists-section">
              <button
                onClick={() => {
                  const isOpening = !expandedCustomSection;
                  setExpandedCustomSection(isOpening);
                  if (isOpening) scrollSectionIntoView('custom-checklists-section');
                }}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <h2 className="text-lg font-semibold">Egna listor (laddar...)</h2>
                {expandedCustomSection ? (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
              {expandedCustomSection && (
              <div className="space-y-2 mt-3">
                {[1, 2].map((i) => (
                  <div key={i} className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg p-2.5 bg-muted/30 animate-pulse">
                          <div className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <div className="h-5 bg-muted/30 rounded animate-pulse w-32 mb-2" />
                          <div className="flex items-center gap-3">
                            <div className="w-[125px] h-3 rounded-full bg-muted/30 animate-pulse" />
                            <div className="h-4 w-12 bg-muted/30 rounded animate-pulse" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Ny lista knapp i skeleton loader */}
                <div className="mt-3">
                  <div className="w-full h-10 bg-muted/30 rounded-lg animate-pulse" />
                </div>
              </div>
              )}
            </div>
          )}
          {!areaFilter && levelFilter === 'all' && completedDefaultItemsCount >= 3 && (
            <div id="custom-checklists-section">
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      const isOpening = !expandedCustomSection;
                      setExpandedCustomSection(isOpening);
                      if (isOpening) scrollSectionIntoView('custom-checklists-section');
                    }
                  }}
                onClick={() => {
                  const isOpening = !expandedCustomSection;
                  setExpandedCustomSection(isOpening);
                  if (isOpening) scrollSectionIntoView('custom-checklists-section');
                }}
                className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
              >
                <h2 className="text-lg font-semibold">
                  Egna listor {customLists.length > 0 && `(${customLists.length} ${customLists.length === 1 ? 'lista' : 'listor'})`}
                </h2>
                <div className="flex flex-col items-center gap-1">
                  {expandedCustomSection ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (customLists.length === 0) return;

                      if (allCustomListsExpanded) {
                        setExpandedCustomLists(new Set());
                      } else {
                        setExpandedCustomSection(true);
                        setExpandedCustomLists(new Set(customLists.map((list) => list.id)));
                        scrollSectionIntoView('custom-checklists-section');
                      }
                    }}
                    className="p-0.5 rounded hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={allCustomListsExpanded ? 'Stäng alla listor' : 'Expandera alla listor'}
                    aria-label={allCustomListsExpanded ? 'Stäng alla listor' : 'Expandera alla listor'}
                    disabled={customLists.length === 0}
                  >
                    {allCustomListsExpanded ? (
                      <ChevronsDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronsRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              </div>
              {expandedCustomSection && (
              <div className="space-y-2 mt-3">
                {customLists.slice().sort((a, b) => {
                  const oa = typeof a.order === 'number' ? a.order : 0;
                  const ob = typeof b.order === 'number' ? b.order : 0;
                  if (oa !== ob) return oa - ob;
                  return a.createdAt.localeCompare(b.createdAt);
                }).map(list => {
                  const isExpanded = expandedCustomLists.has(list.id);
                  const listItems = getCustomItemsForList(list.id);
                  const listTotal = listItems.length;
                  const listCompleted = listItems.filter(i => i.completed).length;
                  const listProgress = listTotal > 0 ? Math.round((listCompleted / listTotal) * 100) : 0;
                  const listColor = list.color || LIST_COLORS[0];

                  

                  return (
                    <div key={list.id} id={`custom-list-${list.id}`} className="rounded-xl border border-border bg-card overflow-hidden">
                      <div className="p-3">
                        <div
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              const next = new Set(expandedCustomLists);
                              const isOpening = !next.has(list.id);
                              if (next.has(list.id)) next.delete(list.id);
                              else next.add(list.id);
                              setExpandedCustomLists(next);
                              if (isOpening) {
                                setTimeout(() => {
                                  const el = document.getElementById(`custom-list-${list.id}`);
                                  if (el) {
                                    const offset = window.innerWidth < 768 ? 64 : 16;
                                    const top = el.getBoundingClientRect().top + window.scrollY - offset;
                                    window.scrollTo({ top, behavior: 'smooth' });
                                  }
                                }, 50);
                              }
                            }
                          }}
                          onClick={() => {
                            const next = new Set(expandedCustomLists);
                            const isOpening = !next.has(list.id);
                            if (next.has(list.id)) next.delete(list.id);
                            else next.add(list.id);
                            setExpandedCustomLists(next);
                            if (isOpening) {
                              setTimeout(() => {
                                const el = document.getElementById(`custom-list-${list.id}`);
                                if (el) {
                                  const offset = window.innerWidth < 768 ? 64 : 16;
                                  const top = el.getBoundingClientRect().top + window.scrollY - offset;
                                  window.scrollTo({ top, behavior: 'smooth' });
                                }
                              }, 50);
                            }
                          }}
                          className="w-full flex items-center justify-between hover:bg-muted/50 transition-colors cursor-pointer p-2 -m-2 rounded-lg"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="rounded-lg p-2.5 flex items-center justify-center" style={{ backgroundColor: `${listColor}20`, color: listColor }}>
                              <ClipboardList className="h-5 w-5" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <div className="font-medium soft-wrap">{list.name}</div>
                              <div className="flex items-center gap-3 mt-1">
                                <div
                                  className="w-[125px] h-3 rounded-full bg-muted/30 border border-border overflow-hidden"
                                  role="progressbar"
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-valuenow={listProgress}
                                  aria-label={`Progress för lista ${list.name}`}
                                >
                                  <div
                                    className="h-full rounded-full transition-all"
                                    style={{ width: `${listProgress}%`, backgroundColor: listColor }}
                                  />
                                </div>
                                <span className="text-sm text-muted-foreground">{listCompleted}/{listTotal}</span>
                                {list.recurring && (
                                  <div title={`Återkommande ${list.recurringType === 'daily' ? 'dagligen' : 'veckovis'}`}>
                                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className={`h-4 w-4 text-muted-foreground ml-2 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                        </div>

                        {/* Action buttons below progress bar */}
                        <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                          <button
                            onClick={(e) => { e.stopPropagation(); useCustomChecklistStore.getState().moveListUp(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Flytta upp"
                          >
                            <ChevronUp className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); useCustomChecklistStore.getState().moveListDown(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Flytta ner"
                          >
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              setRenamingListId(list.id); 
                              setRenamingListName(list.name); 
                              setRenamingListShowOnDashboard(list.showOnDashboard || false);
                              setRenamingListRecurring(list.recurring || false);
                              setRenamingListRecurringType(list.recurringType || 'daily');
                              setRenamingListRecurringTime(list.recurringTime ?? 0);
                              setRenamingListRecurringDay(list.recurringDay ?? 0);
                              setRenamingListColor(list.color || LIST_COLORS[0]);
                            }}
                            className="p-2 rounded hover:bg-muted"
                            title="Redigera lista"
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmUncheckAllListId(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Avmarkera alla"
                          >
                            <Square className="h-4 w-4 text-muted-foreground" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmClearListId(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Rensa avklarade"
                          >
                            <Minus className="h-4 w-4 text-white" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeletingListId(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Radera lista"
                          >
                            <Trash2 className="h-4 w-4 text-danger" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setFullscreenListId(list.id); }}
                            className="p-2 rounded hover:bg-muted"
                            title="Visa i helskärm"
                          >
                            <Maximize2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-border p-3">
                          <div className="space-y-2">
                                {getCustomItemsForList(list.id).filter(ci => (showCompleted ? true : !ci.completed)).map(item => (
                              <div key={item.id} onClick={() => {
                                // Premium required to check items in custom lists
                                if (!hasPremiumAccess) {
                                  setPremiumFeature('Egna checklistor');
                                  setShowPremiumModal(true);
                                  return;
                                }
                                toggleCustomItem(item.id);
                              }} className={`flex items-center gap-2 py-2 pr-2 pl-5 rounded cursor-pointer ${item.completed ? 'opacity-60' : ''}`}>
                                <div
                                  className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all"
                                  style={{
                                    borderColor: listColor,
                                    backgroundColor: item.completed ? listColor : 'transparent',
                                  }}
                                >
                                  {item.completed && <Check className="h-3 w-3 text-white" />}
                                </div>
                                <div className="flex-1 min-w-0 overflow-hidden">
                                  <span className={`font-medium block soft-wrap ${item.completed ? 'line-through' : ''}`}>{item.text}</span>
                                </div>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); useCustomChecklistStore.getState().moveItemUp(item.id); }}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Flytta upp"
                                  >
                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); useCustomChecklistStore.getState().moveItemDown(item.id); }}
                                    className="p-2 rounded hover:bg-muted"
                                    title="Flytta ner"
                                  >
                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setEditingItemId(item.id); setEditingItemText(item.text); }} className="p-2 rounded hover:bg-muted">
                                    <Pencil className="h-4 w-4 text-muted-foreground" />
                                  </button>
                                  <button onClick={(e) => { e.stopPropagation(); setDeletingItemId(item.id); }} className="p-2 rounded hover:bg-muted">
                                    <Trash2 className="h-4 w-4 text-danger" />
                                  </button>
                                </div>
                              </div>
                            ))}

                            <div className="pt-2">
                              <AddCustomItem 
                                listId={list.id} 
                                createItem={createCustomItem} 
                                hasPremiumAccess={hasPremiumAccess}
                                currentItemCount={getCustomItemsForList(list.id).length}
                                onPremiumRequired={() => {
                                  setPremiumFeature('Egna checklistor');
                                  setShowPremiumModal(true);
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {/* Tom-state när inga egna listor finns */}
                {customLists.length === 0 && !isSyncing && (
                  <div className="text-center py-6 text-muted-foreground">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">
                      Skapa dina egna checklistor för specifika behov och projekt.
                    </p>
                    {!hasPremiumAccess && (
                      <p className="text-xs mt-2 text-primary">
                        Premium krävs för egna checklistor
                      </p>
                    )}
                  </div>
                )}
                
                {/* Ny lista knapp */}
                <div className="mt-3">
                  <button
                    onClick={() => {
                      // Free users cannot create custom lists at all
                      if (!hasPremiumAccess) {
                        setPremiumFeature('Egna checklistor');
                        setShowPremiumModal(true);
                        return;
                      }
                      // Premium users limited to 50 lists
                      if (hasPremiumAccess && customLists.length >= PREMIUM_LISTS) {
                        return; // Silent block at premium limit
                      }
                      setShowCreateModal(true);
                    }}
                    className="w-full px-4 py-2 rounded-lg border border-dashed border-border transition-colors text-sm flex items-center justify-center gap-2"
                    style={{ backgroundColor: '#67724e', color: '#fff' }}
                    onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                    onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                  >
                    <Plus className="h-4 w-4" />
                    Ny lista
                  </button>
                </div>
              </div>
              )}
            </div>
          )}

        {/* Create custom list modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Skapa ny lista</h2>
                <button onClick={() => { setShowCreateModal(false); setNewListName(''); setNewListShowOnDashboard(false); }} className="p-1 rounded hover:bg-muted">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  value={newListName}
                  maxLength={50}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="Namn på ny lista"
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  autoFocus
                />
                <div className="text-xs text-muted-foreground mt-1">{newListName.length}/50</div>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newListShowOnDashboard}
                    onChange={(e) => setNewListShowOnDashboard(e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm">Visa på Översikt</span>
                </label>
              </div>

              <div className="mb-4 border-t border-border pt-4">
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={newListRecurring}
                    onChange={(e) => {
                      setNewListRecurring(e.target.checked);
                      if (!e.target.checked) {
                        // Reset recurring settings when disabling
                        setNewListRecurringType('daily');
                        setNewListRecurringTime(0);
                        setNewListRecurringDay(0);
                      }
                    }}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm font-medium">Återkommande</span>
                </label>

                {newListRecurring && (
                  <div className="ml-6 space-y-3">
                    {/* Recurring Type Selector */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Frekvens</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setNewListRecurringType('daily')}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                            newListRecurringType === 'daily'
                              ? 'border-[#67724e] bg-[#67724e]/10 text-[#67724e]'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          Dagligen
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewListRecurringType('weekly')}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                            newListRecurringType === 'weekly'
                              ? 'border-[#67724e] bg-[#67724e]/10 text-[#67724e]'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          Veckovis
                        </button>
                      </div>
                    </div>

                    {/* Daily Time Picker */}
                    {newListRecurringType === 'daily' && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Förnyelse vid klockslag
                        </label>
                        <select
                          value={newListRecurringTime}
                          onChange={(e) => setNewListRecurringTime(Number(e.target.value))}
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {String(i).padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Weekly Day Picker */}
                    {newListRecurringType === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Förnyelse varje
                        </label>
                        <select
                          value={newListRecurringDay}
                          onChange={(e) => setNewListRecurringDay(Number(e.target.value))}
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                        >
                          <option value={0}>Söndag</option>
                          <option value={1}>Måndag</option>
                          <option value={2}>Tisdag</option>
                          <option value={3}>Onsdag</option>
                          <option value={4}>Torsdag</option>
                          <option value={5}>Fredag</option>
                          <option value={6}>Lördag</option>
                        </select>
                        <div className="text-xs text-muted-foreground mt-1">
                          Listan förnyas kl 00:00
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground">
                      När listan förnyas avmarkeras alla aktiviteter automatiskt
                    </div>
                  </div>
                )}
              </div>
              
              {/* Color Picker */}
              <div className="mb-4 border-t border-border pt-4">
                <label className="block text-sm font-medium mb-3">Färg</label>
                <div className="flex gap-2">
                  {LIST_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewListColor(color)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        newListColor === color ? 'ring-2 ring-offset-2 ring-offset-background' : 'hover:scale-110'
                      }`}
                      style={{ 
                        backgroundColor: color
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { 
                  setShowCreateModal(false); 
                  setNewListName(''); 
                  setNewListShowOnDashboard(false);
                  setNewListRecurring(false);
                  setNewListRecurringType('daily');
                  setNewListRecurringTime(0);
                  setNewListRecurringDay(0);
                  setNewListColor(LIST_COLORS[0]);
                }} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Avbryt</button>
                <button 
                  onClick={async () => {
                  if (!newListName.trim()) return;
                  const created = await createCustomList(
                    newListName.trim(), 
                    newListShowOnDashboard,
                    newListRecurring,
                    newListRecurringType,
                    newListRecurringType === 'daily' ? newListRecurringTime : undefined,
                    newListRecurringType === 'weekly' ? newListRecurringDay : undefined,
                    newListColor
                  );
                  if (created.id) {
                    const id = created.id;
                    const next = new Set(expandedCustomLists);
                    next.add(id);
                    setExpandedCustomLists(next);
                  }
                  setShowCreateModal(false);
                  setNewListName('');
                  setNewListShowOnDashboard(false);
                  setNewListRecurring(false);
                  setNewListRecurringType('daily');
                  setNewListRecurringTime(0);
                  setNewListRecurringDay(0);
                  setNewListColor(LIST_COLORS[0]);
                }} 
                  className="flex-1 px-4 py-2 rounded-lg text-white transition-colors"
                  style={{ backgroundColor: '#67724e' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#56633f'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#67724e'}
                >Skapa</button>
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Checklists by area (Standardlistor) */}
        <div id="default-checklists-section">
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                const isOpening = !expandedDefaultSection;
                setExpandedDefaultSection(isOpening);
                if (isOpening) scrollSectionIntoView('default-checklists-section');
              }
            }}
            onClick={() => {
              const isOpening = !expandedDefaultSection;
              setExpandedDefaultSection(isOpening);
              if (isOpening) scrollSectionIntoView('default-checklists-section');
            }}
            className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors"
          >
            <h2 className="text-lg font-semibold">Standardlistor</h2>
            <div className="flex flex-col items-center gap-1">
              {expandedDefaultSection ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground" />
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (areasWithItems.length === 0) return;

                  if (allDefaultAreasExpanded) {
                    setExpandedAreas(new Set());
                  } else {
                    setExpandedDefaultSection(true);
                    setExpandedAreas(new Set(areasWithItems));
                    scrollSectionIntoView('default-checklists-section');
                  }
                }}
                className="p-0.5 rounded hover:bg-muted/70 disabled:opacity-50 disabled:cursor-not-allowed"
                title={allDefaultAreasExpanded ? 'Stäng alla standardlistor' : 'Expandera alla standardlistor'}
                aria-label={allDefaultAreasExpanded ? 'Stäng alla standardlistor' : 'Expandera alla standardlistor'}
                disabled={defaultListsCount === 0}
              >
                {allDefaultAreasExpanded ? (
                  <ChevronsDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronsRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>
        </div>
        {expandedDefaultSection && (
        <div className="space-y-4">
          {(Object.keys(areaConfig) as AreaKey[]).map(area => {
            const areaItems = groupedItems[area];
            if (areaItems.length === 0) return null;
          
            const config = areaConfig[area];
            const Icon = config.icon;
            const isExpanded = expandedAreas.has(area);
            const completedCount = areaItems.filter(i => isItemCompleted(i.id)).length;
            const progress = Math.round((completedCount / areaItems.length) * 100);

            return (
              <div key={area} id={`area-${area}`} className="rounded-xl border border-border bg-card overflow-hidden">
                {/* Area header */}
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleAreaExpansion(area);
                    }
                  }}
                  onClick={() => toggleAreaExpansion(area)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-muted/50 transition-colors"
                >
                  <div 
                    className="rounded-lg p-2.5"
                    style={{ backgroundColor: `${config.color}20` }}
                  >
                    <Icon className="h-5 w-5" style={{ color: config.color }} />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="font-semibold">{t(`prepAreas.${area}`)}</h3>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex-1 h-2 max-w-[200px] rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${progress}%`, backgroundColor: config.color }}
                        />
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {completedCount}/{areaItems.length}
                      </span>
                    </div>
                  </div>
                  <Link
                    to={`/app/knowledge?area=${area}`}
                    onClick={(e) => e.stopPropagation()}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Visa i Kunskap"
                  >
                    <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                  </Link>
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>

                {/* Items */}
                {isExpanded && (
                  <div className="border-t border-border divide-y divide-border">
                    {areaItems.map(item => {
                      const checked = isItemCompleted(item.id);
                      return (
                        <div
                          key={item.id}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleItem(item.id);
                            }
                          }}
                          onClick={() => toggleItem(item.id)}
                          className={`flex items-start gap-4 py-4 pr-4 pl-7 cursor-pointer hover:bg-muted/30 transition-colors ${
                            checked ? 'opacity-60' : ''
                          }`}
                        >
                          <div className="pt-0.5">
                            <div
                              className="w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer"
                              style={{
                                borderColor: config.color,
                                backgroundColor: checked ? config.color : 'transparent',
                              }}
                            >
                              {checked && <Check className="h-3 w-3 text-white" />}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className={`font-medium ${checked ? 'line-through' : ''}`}>
                                {item.text}
                              </span>
                              <span className={`px-2 py-0.5 rounded-full text-xs border ${levelColors[item.level]}`}>
                                {levelLabels[item.level]}
                              </span>
                            </div>
                            {item.description && (
                              <p className="text-sm text-muted-foreground mt-1 lg:max-w-3xl">{item.description}</p>
                            )}
                          </div>
                          <button
                            onClick={(e) => {
                                e.stopPropagation();
                                  setShoppingItemName(item.text);
                                  setShowShoppingModal(true);
                            }}
                            className="p-2 rounded-lg hover:bg-blue-500/20 hover:text-blue-400 transition-colors flex-shrink-0"
                            title="Lägg till i inköpslista"
                          >
                            <ShoppingCart className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="rounded-xl border-2 border-dashed border-border p-8 text-center">
            <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">{t('checklists.noItems')}</p>
            <button
              onClick={() => {
                setSearchParams({});
                setLevelFilter('all');
                setShowCompleted(true);
              }}
              className="mt-4 text-primary hover:underline"
            >
              {t('checklists.resetFilters')}
            </button>
          </div>
        )}

        <AddToShoppingListModal
          open={!!showShoppingModal && !!shoppingItemName}
          itemName={shoppingItemName ?? ''}
          onClose={() => {
            setShowShoppingModal(false);
            setShoppingItemName(null);
          }}
        />

        {/* Rename custom list modal */}
        {renamingListId && (
          <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
            <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Redigera lista</h2>
                <button onClick={() => { setRenamingListId(null); setRenamingListName(''); setRenamingListShowOnDashboard(false); }} className="p-1 rounded hover:bg-muted">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  value={renamingListName}
                  maxLength={50}
                  onChange={(e) => setRenamingListName(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  autoFocus
                />
                <div className="text-xs text-muted-foreground mt-1">{renamingListName.length}/50</div>
              </div>

              <div className="mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={renamingListShowOnDashboard}
                    onChange={(e) => setRenamingListShowOnDashboard(e.target.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm">Visa på Översikt</span>
                </label>
              </div>

              <div className="mb-4 border-t border-border pt-4">
                <label className="flex items-center gap-2 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    checked={renamingListRecurring}
                    onChange={(e) => {
                      setRenamingListRecurring(e.target.checked);
                      if (!e.target.checked) {
                        setRenamingListRecurringType('daily');
                        setRenamingListRecurringTime(0);
                        setRenamingListRecurringDay(0);
                      }
                    }}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm font-medium">Återkommande</span>
                </label>

                {renamingListRecurring && (
                  <div className="ml-6 space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-2">Frekvens</label>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setRenamingListRecurringType('daily')}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                            renamingListRecurringType === 'daily'
                              ? 'border-[#67724e] bg-[#67724e]/10 text-[#67724e]'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          Dagligen
                        </button>
                        <button
                          type="button"
                          onClick={() => setRenamingListRecurringType('weekly')}
                          className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                            renamingListRecurringType === 'weekly'
                              ? 'border-[#67724e] bg-[#67724e]/10 text-[#67724e]'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          Veckovis
                        </button>
                      </div>
                    </div>

                    {renamingListRecurringType === 'daily' && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Förnyelse vid klockslag
                        </label>
                        <select
                          value={renamingListRecurringTime}
                          onChange={(e) => setRenamingListRecurringTime(Number(e.target.value))}
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>
                              {String(i).padStart(2, '0')}:00
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {renamingListRecurringType === 'weekly' && (
                      <div>
                        <label className="block text-sm font-medium mb-2">
                          Förnyelse varje
                        </label>
                        <select
                          value={renamingListRecurringDay}
                          onChange={(e) => setRenamingListRecurringDay(Number(e.target.value))}
                          className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                        >
                          <option value={0}>Söndag</option>
                          <option value={1}>Måndag</option>
                          <option value={2}>Tisdag</option>
                          <option value={3}>Onsdag</option>
                          <option value={4}>Torsdag</option>
                          <option value={5}>Fredag</option>
                          <option value={6}>Lördag</option>
                        </select>
                        <div className="text-xs text-muted-foreground mt-1">
                          Listan förnyas kl 00:00
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-muted-foreground">
                      När listan förnyas avmarkeras alla aktiviteter automatiskt
                    </div>
                  </div>
                )}
              </div>
              
              {/* Color Picker */}
              <div className="mb-4 border-t border-border pt-4">
                <label className="block text-sm font-medium mb-3">Färg</label>
                <div className="flex gap-2">
                  {LIST_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setRenamingListColor(color)}
                      className={`w-8 h-8 rounded-full transition-all ${
                        renamingListColor === color ? 'ring-2 ring-offset-2 ring-offset-background' : 'hover:scale-110'
                      }`}
                      style={{ 
                        backgroundColor: color
                      }}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { 
                  setRenamingListId(null); 
                  setRenamingListName(''); 
                  setRenamingListShowOnDashboard(false);
                  setRenamingListRecurring(false);
                  setRenamingListRecurringType('daily');
                  setRenamingListRecurringTime(0);
                  setRenamingListRecurringDay(0);
                  setRenamingListColor(LIST_COLORS[0]);
                }} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Avbryt</button>
                <button 
                  onClick={() => {
                    if (!renamingListName.trim() || !renamingListId) return;
                    updateCustomList(renamingListId, {
                      name: renamingListName.trim(),
                      showOnDashboard: renamingListShowOnDashboard,
                      recurring: renamingListRecurring,
                      recurringType: renamingListRecurringType,
                      recurringTime: renamingListRecurringType === 'daily' ? renamingListRecurringTime : undefined,
                      recurringDay: renamingListRecurringType === 'weekly' ? renamingListRecurringDay : undefined,
                      color: renamingListColor,
                    });
                    setRenamingListId(null);
                    setRenamingListName('');
                    setRenamingListShowOnDashboard(false);
                    setRenamingListRecurring(false);
                    setRenamingListRecurringType('daily');
                    setRenamingListRecurringTime(0);
                    setRenamingListRecurringDay(0);
                    setRenamingListColor(LIST_COLORS[0]);
                  }} 
                  className="flex-1 px-4 py-2 rounded-lg transition-colors text-white"
                  style={{ backgroundColor: '#67724e' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                >
                  Spara
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirm delete custom list modal */}
        <ConfirmationModal
          open={!!deletingListId}
          title="Radera lista"
          description="Är du säker på att du vill radera den här listan? Denna åtgärd går inte att ångra."
          confirmLabel="Radera"
          cancelLabel="Avbryt"
          danger
          onCancel={() => setDeletingListId(null)}
          onConfirm={() => {
            if (deletingListId) deleteCustomList(deletingListId);
            setDeletingListId(null);
          }}
        />

        {/* Confirm delete custom item modal */}
        <ConfirmationModal
          open={!!deletingItemId}
          title="Radera uppgift"
          description="Är du säker på att du vill radera den här uppgiften?"
          confirmLabel="Radera"
          cancelLabel="Avbryt"
          danger
          onCancel={() => setDeletingItemId(null)}
          onConfirm={() => {
            if (deletingItemId) deleteCustomItem(deletingItemId);
            setDeletingItemId(null);
          }}
        />

        {/* Confirm uncheck all items modal */}
        {confirmUncheckAllListId ? (() => {
          const list = customLists.find(l => l.id === confirmUncheckAllListId);
          return (
            <ModalBackdrop>
              <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Square className="h-6 w-6 text-blue-500" />
                  </div>
                  <h2 className="text-xl font-semibold">Avmarkera alla uppgifter</h2>
                </div>
                <p className="text-muted-foreground mb-2">
                  Vill du avmarkera alla markerade uppgifter i <span className="font-medium text-foreground">{list?.name}</span>?
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Detta avmarkerar alla uppgifter som är markerade som avklarade.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setConfirmUncheckAllListId(null); }}
                    className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={async () => { await uncheckAllForList(confirmUncheckAllListId as string); setConfirmUncheckAllListId(null); }}
                    className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
                  >
                    Avmarkera
                  </button>
                </div>
              </div>
            </ModalBackdrop>
          );
        })() : null}

        {/* Confirm clear completed items modal */}
        {confirmClearListId ? (() => {
          const list = customLists.find(l => l.id === confirmClearListId);
          return (
            <ModalBackdrop>
              <div className="w-full max-w-sm rounded-xl bg-card border border-border p-6 my-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Minus className="h-6 w-6 text-yellow-500" />
                  </div>
                  <h2 className="text-xl font-semibold">Rensa avklarade uppgifter</h2>
                </div>
                <p className="text-muted-foreground mb-2">
                  Vill du ta bort alla avklarade uppgifter från <span className="font-medium text-foreground">{list?.name}</span>?
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Detta raderar endast uppgifter som redan är markerade som avklarade.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setConfirmClearListId(null); }}
                    className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    Avbryt
                  </button>
                  <button
                    onClick={async () => { await clearCompletedForList(confirmClearListId as string); setConfirmClearListId(null); }}
                    className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Rensa
                  </button>
                </div>
              </div>
            </ModalBackdrop>
          );
        })() : null}

        {/* Edit custom item modal */}
        {editingItemId && (
          <div className="fixed z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, margin: 0 }}>
            <div className="w-full max-w-md rounded-xl bg-card border border-border p-6 my-8 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Redigera uppgift</h2>
                <button onClick={() => { setEditingItemId(null); setEditingItemText(''); }} className="p-1 rounded hover:bg-muted">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  value={editingItemText}
                  maxLength={50}
                  onChange={(e) => setEditingItemText(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  autoFocus
                />
                <div className="text-xs text-muted-foreground mt-1">{editingItemText.length}/50</div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => { setEditingItemId(null); setEditingItemText(''); }} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Avbryt</button>
                <button 
                  onClick={() => {
                    if (!editingItemText.trim() || !editingItemId) return;
                    updateCustomItem(editingItemId, editingItemText.trim());
                    setEditingItemId(null);
                    setEditingItemText('');
                  }} 
                  className="flex-1 px-4 py-2 rounded-lg transition-colors text-white"
                  style={{ backgroundColor: '#67724e' }}
                  onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
                  onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
                >
                  Spara
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Premium Required Modal */}
        <PremiumRequiredModal
          open={showPremiumModal}
          onClose={() => setShowPremiumModal(false)}
          feature={premiumFeature}
        />

        {/* Fullscreen Modal */}
        {fullscreenListId && (() => {
          const list = customLists.find(l => l.id === fullscreenListId);
          if (!list) return null;
          
          const listItems = getCustomItemsForList(list.id);
          
          return (
            <ModalBackdrop
              onClick={() => setFullscreenListId(null)}
              className="items-start overflow-y-auto"
            >
              <div 
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-4xl h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] mt-8 mb-0 translate-y-6 bg-card border border-border rounded-xl flex flex-col overflow-hidden"
              >
                {/* Fixed header */}
                <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
                  <h2 className="text-2xl font-bold soft-wrap">{list.name}</h2>
                  <button
                    onClick={() => setFullscreenListId(null)}
                    className="p-2 rounded hover:bg-muted"
                    title="Stäng"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                {/* Scrollable content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-2">
                    {listItems.map(item => (
                      <div 
                        key={item.id}
                        onClick={() => {
                          if (!hasPremiumAccess) {
                            setPremiumFeature('Egna checklistor');
                            setShowPremiumModal(true);
                            return;
                          }
                          toggleCustomItem(item.id);
                        }}
                        className={`flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors ${item.completed ? 'opacity-60' : ''}`}
                      >
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 ${item.completed ? 'border-orange-500 bg-orange-500' : 'border-muted-foreground'}`}>
                          {item.completed && <Check className="h-4 w-4 text-white" />}
                        </div>
                        <span className={`font-medium soft-wrap ${item.completed ? 'line-through' : ''}`}>{item.text}</span>
                      </div>
                    ))}
                    
                    {listItems.length === 0 && (
                      <div className="text-center text-muted-foreground py-8">
                        Inga uppgifter i listan än
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ModalBackdrop>
          );
        })()}
      </div>
    );
  }

function AddCustomItem({ 
  listId, 
  createItem, 
  hasPremiumAccess, 
  currentItemCount, 
  onPremiumRequired 
}: { 
  listId: string; 
  createItem: (listId: string, text: string) => unknown; 
  hasPremiumAccess: boolean;
  currentItemCount: number;
  onPremiumRequired: () => void;
}) {
  const [text, setText] = useState('');
  const handleAdd = () => {
    if (!text.trim() || text.trim().length < 3) return;
    
    // Premium required for custom lists - this check is redundant as free users can't create lists,
    // but kept for safety
    if (!hasPremiumAccess) {
      onPremiumRequired();
      return;
    }
    
    // Premium users limited to 500 items per list
    if (hasPremiumAccess && currentItemCount >= 500) {
      return; // Silent block at premium limit
    }
    
    createItem(listId, text.trim());
    setText('');
  };

  return (
    <div className="flex gap-2">
      <div className="flex-1">
        <input
          type="text"
          value={text}
          maxLength={50}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder="Lägg till uppgift"
          className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm"
        />
        <div className="text-xs text-muted-foreground mt-1">{text.length}/50</div>
      </div>
      <button
        aria-label="Lägg till uppgift"
        onClick={handleAdd}
        className="px-3 py-2 rounded-lg"
        style={{ backgroundColor: '#67724e', color: '#fff' }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#56633f')}
        onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.backgroundColor = '#67724e')}
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

interface ChecklistAreaFilterDropdownProps {
  value: AreaKey | 'all';
  onChange: (value: AreaKey | 'all') => void;
  areas: AreaKey[];
}

function ChecklistAreaFilterDropdown({ value, onChange, areas }: ChecklistAreaFilterDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabel = value === 'all'
    ? t('checklists.filter.allAreas')
    : t(`prepAreas.${value}`);

  return (
    <div ref={dropdownRef} className="relative flex-1 sm:flex-none min-w-[160px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 rounded-lg border bg-input text-foreground text-sm flex items-center justify-between gap-2 hover:bg-muted transition-colors ${
          value !== 'all'
            ? 'border-slate-300 shadow-[0_0_8px_rgba(148,163,184,0.6)]'
            : 'border-border'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedLabel}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value !== 'all' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('all');
                setIsOpen(false);
              }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              aria-label={t('checklists.filter.allAreas')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange('all');
              setIsOpen(false);
            }}
            className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
              value === 'all' ? 'bg-muted font-medium' : ''
            }`}
          >
            {t('checklists.filter.allAreas')}
          </button>

          {areas.map((area) => (
            <button
              key={area}
              type="button"
              onClick={() => {
                onChange(area);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                value === area ? 'bg-muted font-medium' : ''
              }`}
            >
              {t(`prepAreas.${area}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface ChecklistLevelFilterDropdownProps {
  value: TimeLevel | 'all';
  onChange: (value: TimeLevel | 'all') => void;
  levels: TimeLevel[];
  labels: Record<TimeLevel, string>;
}

function ChecklistLevelFilterDropdown({ value, onChange, levels, labels }: ChecklistLevelFilterDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedLabel = value === 'all'
    ? t('checklists.filter.levelAll')
    : labels[value];

  return (
    <div ref={dropdownRef} className="relative flex-1 sm:flex-none min-w-[160px]">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full px-3 py-2 rounded-lg border bg-input text-foreground text-sm flex items-center justify-between gap-2 hover:bg-muted transition-colors ${
          value !== 'all'
            ? 'border-slate-300 shadow-[0_0_8px_rgba(148,163,184,0.6)]'
            : 'border-border'
        }`}
      >
        <span className="flex items-center gap-2 truncate">
          {selectedLabel}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value !== 'all' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange('all');
                setIsOpen(false);
              }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              aria-label={t('checklists.filter.levelAll')}
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[280px] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              onChange('all');
              setIsOpen(false);
            }}
            className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
              value === 'all' ? 'bg-muted font-medium' : ''
            }`}
          >
            {t('checklists.filter.levelAll')}
          </button>

          {levels.map((level) => (
            <button
              key={level}
              type="button"
              onClick={() => {
                onChange(level);
                setIsOpen(false);
              }}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors ${
                value === level ? 'bg-muted font-medium' : ''
              }`}
            >
              {labels[level]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
